import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Sparkles,
  Target,
  Eye,
  Compass,
  Globe2,
  TrendingUp,
  Tv2,
  Gamepad2,
  ShoppingBag,
  Building2,
  Stethoscope,
  Laptop,
  Landmark,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Layers,
  BarChart3,
  Award,
} from "lucide-react";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Us — KMWAdvertising · Global Media Buying & Performance Agency" },
      {
        name: "description",
        content:
          "Learn how KMWAdvertising connects strategy, media, data, and performance marketing to build scalable growth engines for global brands.",
      },
      { property: "og:title", content: "About Us — KMWAdvertising" },
      {
        property: "og:description",
        content:
          "We Don't Just Buy Media. We Build Growth Engines. Learn about our mission, vision, multi-channel execution, and strategic media expertise.",
      },
    ],
  }),
  component: AboutPage,
});

export function AboutPage() {
  const uniquePillars = [
    {
      title: "Strategic Media Expertise",
      desc: "We don't believe in one-size-fits-all media plans. Audience insights, market research, and performance data back every recommendation.",
      icon: Compass,
      color: "var(--glow-orange)",
      tag: "Data-Backed Strategy",
    },
    {
      title: "Multi-Channel Execution",
      desc: "From digital campaigns and social media advertising to television, radio, print, and outdoor media, we create integrated campaigns that deliver consistent brand experiences.",
      icon: Layers,
      color: "var(--glow-purple)",
      tag: "360° Media Reach",
    },
    {
      title: "Performance-Focused Mindset",
      desc: "Every campaign is measured against clear KPIs. Whether your goal is awareness, lead generation, registrations, website traffic, or conversions, we focus on outcomes that matter.",
      icon: TrendingUp,
      color: "var(--glow-cyan)",
      tag: "KPI & ROAS Driven",
    },
    {
      title: "Global Perspective, Local Understanding",
      desc: "Our experience across emerging and international markets enables us to develop campaigns that resonate with local audiences while maintaining global brand consistency.",
      icon: Globe2,
      color: "var(--glow-orange)",
      tag: "Pan-Regional Footprint",
    },
  ];

  const industriesServed = [
    { name: "Gaming & Entertainment", icon: Gamepad2, tag: "iGaming & Gaming Scale" },
    { name: "FMCG & Consumer Brands", icon: ShoppingBag, tag: "Mass Market Reach" },
    { name: "Hospitality & Tourism", icon: Globe2, tag: "Direct Booking Flights" },
    { name: "Real Estate", icon: Building2, tag: "High-Intent Buyer Leads" },
    { name: "Healthcare", icon: Stethoscope, tag: "Compliant Practitioner Ads" },
    { name: "E-Commerce", icon: TrendingUp, tag: "Full-Funnel Sales" },
    { name: "Technology", icon: Laptop, tag: "Product Adoption & B2B" },
    { name: "Financial Services", icon: Landmark, tag: "Customer Acquisition" },
  ];

  const approachSteps = [
    {
      num: "01",
      title: "Objective & Market Diagnostics",
      desc: "Every campaign begins with understanding your business objectives, audience behavior, competitive landscape, and market opportunities.",
      color: "var(--glow-orange)",
    },
    {
      num: "02",
      title: "Integrated Media Mix Design",
      desc: "We develop integrated media strategies across Digital, TV, OOH, and Radio designed to maximize reach, engagement, leads, or brand awareness.",
      color: "var(--glow-purple)",
    },
    {
      num: "03",
      title: "Data-Driven Buying Execution",
      desc: "We negotiate, place, and launch media investments leveraging market relationships and algorithmic programmatic bidding exchanges.",
      color: "var(--glow-cyan)",
    },
    {
      num: "04",
      title: "Continuous Optimization & Scaling",
      desc: "Our process is driven by data, refined by experience, and optimized through continuous real-time performance analysis and KPI tracking.",
      color: "var(--glow-orange)",
    },
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-16 md:py-24 border-b border-white/5">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)]" />
          <div className="glow-orb right-[-100px] top-30 h-[380px] w-[380px] bg-[var(--glow-orange)]" />

          <div className="container-x relative z-10">
            {/* Breadcrumb & Badge */}
            <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">Home</Link>
              <span>/</span>
              <span className="text-[var(--glow-orange)] font-semibold">About Us</span>
            </div>

            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-3.5 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-5">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--glow-orange)] shadow-[0_0_8px_var(--glow-orange)] animate-pulse" />
                ABOUT KMWADVERTISING
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                We Don't Just Buy Media. <br />
                <span className="text-gradient">We Build Growth Engines.</span>
              </h1>

              <p className="mt-6 text-base sm:text-lg md:text-xl text-white/90 font-medium leading-relaxed">
                KMW Advertising is a full-service media planning, media buying, and performance marketing agency that helps brands connect with the right audience at the right time through the right channels.
              </p>

              <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                In a world where consumers are exposed to thousands of messages every day, visibility alone isn't enough. Brands need strategic communication, intelligent media investments, measurable performance, and a partner that understands how to navigate both traditional and digital ecosystems. That's where KMWA comes in.
              </p>
            </div>

            {/* Featured Story Box */}
            <div className="mt-10 p-6 sm:p-8 rounded-3xl glass-strong border border-white/10 relative overflow-hidden max-w-4xl shadow-2xl">
              <div className="absolute top-0 right-0 h-40 w-40 bg-[var(--glow-purple)] blur-3xl opacity-15 pointer-events-none" />
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-[var(--glow-orange)] mt-1">
                  <Award className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg sm:text-xl font-bold text-white">
                    Bridging Creativity, Strategy, and Media Effectiveness
                  </h3>
                  <p className="mt-2 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Founded with a vision to bridge creativity, strategy, and media effectiveness, KMWAdvertising has evolved into a 360° marketing partner for brands across multiple industries and geographies. From television and radio to digital platforms, social media, search engines, influencer marketing, print, outdoor advertising, and performance campaigns, we help brands create meaningful impact and measurable business growth.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MISSION & VISION DUAL CARDS */}
        <section className="py-16 md:py-24 bg-black/40 border-b border-white/5">
          <div className="container-x">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Our Mission */}
              <div className="rounded-3xl p-8 sm:p-10 glass-strong border border-white/10 relative overflow-hidden shadow-2xl flex flex-col justify-between group hover:border-[var(--glow-purple)]/50 transition duration-500">
                <div className="absolute top-0 right-0 h-48 w-48 bg-[var(--glow-purple)] blur-3xl opacity-10 group-hover:opacity-25 transition-opacity" />
                <div>
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--glow-purple)] font-semibold mb-4">
                    <Target className="h-4 w-4" />
                    OUR MISSION
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-white leading-snug">
                    Helping Brands <span className="text-gradient">Grow Smarter.</span>
                  </h2>
                  <p className="mt-4 text-base sm:text-lg text-white/90 leading-relaxed font-display">
                    "To help brands grow smarter by combining Media Intelligence, Strategic Thinking, and Measurable Performance."
                  </p>
                </div>
                <div className="mt-8 pt-4 border-t border-white/5 text-xs text-muted-foreground font-mono uppercase tracking-wider">
                  Core Purpose & Focus
                </div>
              </div>

              {/* Our Vision */}
              <div className="rounded-3xl p-8 sm:p-10 glass-strong border border-white/10 relative overflow-hidden shadow-2xl flex flex-col justify-between group hover:border-[var(--glow-orange)]/50 transition duration-500">
                <div className="absolute top-0 right-0 h-48 w-48 bg-[var(--glow-orange)] blur-3xl opacity-10 group-hover:opacity-25 transition-opacity" />
                <div>
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--glow-orange)] font-semibold mb-4">
                    <Eye className="h-4 w-4" />
                    OUR VISION
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-white leading-snug">
                    Preferred Media & <span className="text-gradient">Marketing Partner.</span>
                  </h2>
                  <p className="mt-4 text-base sm:text-lg text-white/90 leading-relaxed font-display">
                    "To become the preferred media and marketing partner for ambitious brands seeking scalable growth across digital and traditional channels."
                  </p>
                </div>
                <div className="mt-8 pt-4 border-t border-white/5 text-xs text-muted-foreground font-mono uppercase tracking-wider">
                  Global Scale Aspiration
                </div>
              </div>
            </div>

            {/* Closing Promise Banner */}
            <div className="mt-8 p-6 rounded-2xl bg-white/[0.02] border border-white/10 text-center max-w-4xl mx-auto">
              <p className="text-xs sm:text-sm md:text-base text-foreground/90 font-medium italic">
                "At KMWAdvertising, we don't measure success by impressions alone. We measure it by the growth, visibility, and business impact we create for our clients."
              </p>
            </div>
          </div>
        </section>

        {/* OUR APPROACH */}
        <section className="py-16 md:py-24 border-b border-white/5">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">
                <Sparkles className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
                OUR APPROACH
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
                Integrated Strategy. <span className="text-gradient">Data-Driven Execution.</span>
              </h2>
              <p className="mt-4 text-base sm:text-lg text-white/90 font-medium leading-relaxed">
                Most agencies focus on either creative execution or media buying. We believe true growth happens when strategy, media, data, and creativity work together.
              </p>
            </div>

            {/* 4 Step Process Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {approachSteps.map((step, idx) => (
                <div 
                  key={idx}
                  className="rounded-3xl p-6 bg-[#08080d] border border-white/10 flex flex-col justify-between relative overflow-hidden shadow-xl hover:border-white/20 transition-all duration-300 group"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-2xl font-bold text-gradient" style={{ backgroundImage: `linear-gradient(135deg, ${step.color}, white)` }}>
                      {step.num}
                    </span>
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: step.color, boxShadow: `0 0 10px ${step.color}` }} />
                  </div>
                  <div className="mt-6">
                    <h3 className="font-display text-lg font-bold text-white group-hover:text-[var(--glow-orange)] transition">
                      {step.title}
                    </h3>
                    <p className="mt-2.5 text-xs text-muted-foreground leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                  <div className="mt-6 pt-3 border-t border-white/5 flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                    <span>System Pass 0{idx + 1}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHAT MAKES KMWA UNIQUE */}
        <section className="py-16 md:py-24 bg-black/40 border-b border-white/5">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">
                <Award className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                THE KMWA ADVANTAGE
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
                What Makes <span className="text-gradient">KMWA Unique?</span>
              </h2>
              <p className="mt-3 text-sm sm:text-base text-muted-foreground">
                We combine negotiated buying power with programmatic precision to deliver outcomes that matter.
              </p>
            </div>

            {/* 4 Unique Pillars Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              {uniquePillars.map((p, idx) => {
                const IconComp = p.icon;
                return (
                  <div 
                    key={idx}
                    className="rounded-3xl p-8 glass-strong border border-white/10 flex flex-col justify-between relative overflow-hidden shadow-2xl group hover:border-white/20 transition-all duration-300"
                  >
                    <div 
                      className="absolute -top-24 -right-24 h-48 w-48 rounded-full blur-3xl opacity-10 group-hover:opacity-25 transition-opacity"
                      style={{ backgroundColor: p.color }}
                    />
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white group-hover:scale-110 transition duration-300">
                          <IconComp className="h-6 w-6" style={{ color: p.color }} />
                        </div>
                        <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground bg-white/5 px-3 py-1 rounded-full border border-white/5">
                          {p.tag}
                        </span>
                      </div>
                      <h3 className="font-display text-xl sm:text-2xl font-bold text-white mt-4">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {p.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* INDUSTRIES WE'VE SERVED */}
        <section className="py-16 md:py-24 border-b border-white/5">
          <div className="container-x">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">
                  <BarChart3 className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
                  VERTICAL EXPERIENCE
                </div>
                <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
                  Industries We’ve <span className="text-gradient">Served.</span>
                </h2>
                <p className="mt-3 text-xs sm:text-sm text-muted-foreground">
                  Custom media strategy and execution tailored specifically for high-growth sectors.
                </p>
              </div>
              <Link to="/contact-us" className="btn-hero !py-2.5 !px-5 !text-xs sm:!text-sm shrink-0 w-fit">
                Request Industry Audit <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {industriesServed.map((ind, idx) => {
                const IconComp = ind.icon;
                return (
                  <div 
                    key={idx}
                    className="p-5 rounded-2xl bg-[#08080d] border border-white/10 hover:border-white/20 transition-all duration-300 group flex flex-col justify-between"
                  >
                    <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-orange)] group-hover:scale-110 transition duration-300 mb-3">
                      <IconComp className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <span className="text-[10px] uppercase font-mono tracking-wider text-muted-foreground/70 block">
                        {ind.tag}
                      </span>
                      <h3 className="font-display text-sm sm:text-base font-bold text-white mt-1 group-hover:text-gradient transition">
                        {ind.name}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA BANNER */}
        <section className="py-16 md:py-24 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-15" />
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-orange)] blur-[160px] opacity-10" />

          <div className="container-x relative z-10 text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground bg-white/5 border border-white/10 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
              START YOUR GROWTH ENGINE
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white leading-tight">
              Ready to Turn Media Spend <br />
              <span className="text-gradient">Into Measurable Growth?</span>
            </h2>
            <p className="mt-4 text-xs sm:text-sm md:text-base text-muted-foreground leading-relaxed max-w-xl mx-auto">
              Book a media consultation with our strategy team today. We'll map out a custom cross-channel strategy for your brand.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link to="/contact-us" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm">
                Book a Media Consultation <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/" className="btn-ghost !py-3 !px-6 !text-xs sm:!text-sm">
                Explore Core Services <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
